import { notification } from "antd";
import { takeLatest, put, call } from "redux-saga/effects";
import {
  fetchDevicesSuccess,
  fetchDevicesFailure,
  updateDeviceStatusFailure,
  updateDeviceStatusSuccess,
  commCheckSuccess,
  commCheckFailure,
  getDeviceByIdSuccess,
  getDeviceByIdFailure,
} from "./action";
import {
  FETCH_DEVICES_REQUEST,
  DEVICEID_LOADER_START,
  DEVICEID_LOADER_STOP,
  UPDATE_DEVICE_STATUS_REQUEST,
  ASSIGN_PROJECT_TO_DEVICEID,
  COMM_CHECK_REQUEST,
  LOADER_START,
  LOADER_STOP,
  SET_TIME_REQUEST,
  GET_TIME_REQUEST,
  ERASE_LOGS_REQUEST,
  NUM_OF_LOGS_REQUEST,
  WELD_PARAMETER_DOWNLOAD_REQUEST,
  LOCATION_REQUEST,
  PASSWORD_PROTECTION_REQUEST,
  COMMAND_SUCCESS,
  COMMAND_FAILURE,
  GET_DEVICE_BY_ID_REQUEST,
} from "./types";
import { ADMIN_URL } from "../../config";
import fetch from "../../services/fetch";

const commandEndpoints = {
  [SET_TIME_REQUEST]: "SetTime",
  [GET_TIME_REQUEST]: "GetTime",
  [ERASE_LOGS_REQUEST]: "EraseLogs",
  [NUM_OF_LOGS_REQUEST]: "NumOfLogs",
  [WELD_PARAMETER_DOWNLOAD_REQUEST]: "WeldParameter",
  [LOCATION_REQUEST]: "Location",
  [PASSWORD_PROTECTION_REQUEST]: "PasswordProtection",
};

function* fetchDevicesSaga() {
  try {
    yield put({ type: DEVICEID_LOADER_START });

    const response = yield call(fetch, `${ADMIN_URL}/v1/admin/devices`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (response.status === 403) {
      throw new Error("Unauthorized");
    }

    const data = yield response.json();

    if (data.statusCode === 200) {
      yield put(fetchDevicesSuccess(data.data.deviceList || []));
    } else {
      throw new Error(data.data?.message || "Something went wrong");
    }
  } catch (error) {
    console.error("Fetch devices error:", error);
    yield put(fetchDevicesFailure(error.message));
    notification.error({
      message: "Error fetching devices",
      description: error.message,
    });
  } finally {
    yield put({ type: DEVICEID_LOADER_STOP });
  }
}

function* updateDeviceStatusSaga(action) {
  try {
    yield put({ type: DEVICEID_LOADER_START });

    const { deviceId, status, projectNumber, projectId, projectName } =
      action.payload;

    const response = yield call(fetch, `${ADMIN_URL}/v1/admin/update-status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        deviceId,
        status,
        projectNumber,
        projectId,
        projectName,
      }),
    });

    if (response.status === 403) {
      throw new Error("Unauthorized");
    }

    const data = yield response.json();

    if (data.statusCode === 200) {
      yield put(updateDeviceStatusSuccess({ deviceId, status }));
      notification.success({
        message: "Status updated successfully",
        description: `Device ${deviceId} status updated to ${status}`,
      });
      // Refresh the devices list
      yield put({ type: FETCH_DEVICES_REQUEST });
    } else {
      throw new Error(data.data?.message || "Failed to update device status");
    }
  } catch (error) {
    console.error("Update device status error:", error);
    yield put(updateDeviceStatusFailure(error.message));
    notification.error({
      message: "Error updating device status",
      description: error.message,
    });
  } finally {
    yield put({ type: DEVICEID_LOADER_STOP });
  }
}

function* assignProjectToDeviceId(action) {
  try {
    yield put({ type: DEVICEID_LOADER_START });

    const { deviceIds, projectName, projectNumber, projectId } = action.payload;

    const response = yield call(fetch, `${ADMIN_URL}/v1/admin/assign-project`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        deviceIds,
        projectName,
        projectNumber,
        projectId,
      }),
    });

    if (response.status === 403) {
      throw new Error("Unauthorized");
    }

    const data = yield response.json();

    if (data.statusCode === 200) {
      notification.success({
        message: "Project assigned successfully",
        description: `Project ${projectName} assigned to selected devices`,
      });
      yield put({ type: FETCH_DEVICES_REQUEST });
    } else {
      throw new Error(
        data.data?.message || "Failed to assign project to devices"
      );
    }
  } catch (error) {
    console.error("Assign project to devices error:", error);
    notification.error({
      message: "Error assigning project",
      description: error.message,
    });
  } finally {
    yield put({ type: DEVICEID_LOADER_STOP });
  }
}

function* commCheckSaga(action) {
  try {
    yield put({ type: LOADER_START });
    const { Edge_UID } = action.payload;
    const { onSuccess } = action;
    const response = yield call(fetch, `${ADMIN_URL}/v1/command/CommCheck`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        Edge_UID,
        Command: "CommCheck",
      }),
    });
    if (response.status === 403) {
      throw new Error("Unauthorized");
    }
    const data = yield response.json();
    // Handle both success: true/false and success: 1/0 formats
    if (
      data.success === true ||
      data.success === 1 ||
      data.statusCode === 200
    ) {
      console.log("CommCheck saga success, data:", data.data);
      yield put(commCheckSuccess(data.data));
      notification.success({
        message: "CommCheck Success",
        description: `CommCheck command sent successfully to ${Edge_UID}`,
      });
      // Execute callback if provided
      if (onSuccess && typeof onSuccess === "function") {
        onSuccess(data.data);
      }
    } else {
      throw new Error(data.data?.message || data.message || "CommCheck failed");
    }
  } catch (error) {
    yield put(commCheckFailure(error.message));
    notification.error({
      message: "CommCheck Error",
      description: error.message,
    });
  } finally {
    yield put({ type: LOADER_STOP });
  }
}

function* getDeviceByIdSaga(action) {
  try {
    yield put({ type: LOADER_START });
    const { deviceId } = action.payload;
    console.log("GetDeviceById saga called with deviceId:", deviceId);

    const response = yield call(
      fetch,
      `${ADMIN_URL}/v1/command/device/${deviceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );

    if (response.status === 403) {
      throw new Error("Unauthorized");
    }

    const data = yield response.json();

    // Handle both success: true/false and success: 1/0 formats
    if (
      data.success === true ||
      data.success === 1 ||
      data.statusCode === 200
    ) {
      yield put(getDeviceByIdSuccess(data.data));
    } else {
      throw new Error(data.message || "Failed to fetch device information");
    }
  } catch (error) {
    yield put(getDeviceByIdFailure(error.message));
    notification.error({
      message: "Device Information Error",
      description: error.message,
    });
  } finally {
    yield put({ type: LOADER_STOP });
  }
}

function* handleCommandSaga(action) {
  const { type, payload, onSuccess } = action;
  const endpoint = commandEndpoints[type];

  const commandName =
    type === WELD_PARAMETER_DOWNLOAD_REQUEST ? "WeldParameter" : endpoint;

  if (!endpoint) return;

  try {
    yield put({ type: LOADER_START });

    const response = yield call(fetch, `${ADMIN_URL}/v1/command/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        ...payload,
        Command: commandName,
      }),
    });

    if (response.status === 403) throw new Error("Unauthorized");

    const data = yield response.json();

    if (data.statusCode === 200) {
      yield put({
        type: COMMAND_SUCCESS,
        commandType: type,
        payload: data.data,
      });

      notification.success({
        message: `${endpoint} Success`,
        description: `${endpoint} command successful for ${payload.Edge_UID}`,
      });

      // Execute callback if provided
      if (onSuccess && typeof onSuccess === "function") {
        onSuccess(data.data);
      }
    } else {
      throw new Error(data.data?.message || `${endpoint} command failed`);
    }
  } catch (error) {
    yield put({
      type: COMMAND_FAILURE,
      commandType: type,
      error: error.message,
    });

    notification.error({
      message: `${endpoint} Error`,
      description: error.message,
    });
  } finally {
    yield put({ type: LOADER_STOP });
  }
}

export default function* deviceIdSagas() {
  yield takeLatest(FETCH_DEVICES_REQUEST, fetchDevicesSaga);
  yield takeLatest(UPDATE_DEVICE_STATUS_REQUEST, updateDeviceStatusSaga);
  yield takeLatest(ASSIGN_PROJECT_TO_DEVICEID, assignProjectToDeviceId);
  yield takeLatest(COMM_CHECK_REQUEST, commCheckSaga);
  yield takeLatest(GET_DEVICE_BY_ID_REQUEST, getDeviceByIdSaga);
  yield takeLatest(
    [
      SET_TIME_REQUEST,
      GET_TIME_REQUEST,
      ERASE_LOGS_REQUEST,
      NUM_OF_LOGS_REQUEST,
      WELD_PARAMETER_DOWNLOAD_REQUEST,
      LOCATION_REQUEST,
      PASSWORD_PROTECTION_REQUEST,
    ],
    handleCommandSaga
  );
}
